import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import Form from 'reactstrap/es/Form';
import Input from 'components/Input';
import { Row } from 'reactstrap';
import Button from 'components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LargestCol from 'components/LargestCol';
import Label from 'components/Input/Label';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import BaseListGroup from 'reactstrap/es/ListGroup';
import BaseListGroupItem from 'reactstrap/es/ListGroupItem';
import Badge from 'reactstrap/es/Badge';
import get from 'lodash/get';

const MAX_RESULTS = 50;

const AccountNumber = styled(Badge)`
  font-size: 0.9em;

  @media screen and (max-width: ${prop('theme.breakpoints.small')}) {
    display: none;
  }
`;

const StyledAccountNumber = styled(AccountNumber)`
  font-size: 0.6em;
  margin-left: 0.25rem;
  display: none;

  @media screen and (max-width: ${prop('theme.breakpoints.small')}) {
    display: inline;
  }
`;

const ListGroup = styled(BaseListGroup)`
  max-height: 13.5rem;
  overflow-y: auto;
  margin-bottom: 1rem;
`;

const ListGroupItem = styled(BaseListGroupItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    display: block;
  }
`;

const SelectUserButton = styled(Button)`
  font-size: 0.75em;
  margin: 0 0.25em !important;
  padding: 0.25em 1em !important;

  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    margin-top: 1em !important;
  }

  @media screen and (max-width: ${prop('theme.breakpoints.small')}) {
    font-size: 0.6em;
  }

  &:first-of-type {
    margin-left: 1em !important;

    @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
      margin-left: 0 !important;
    }
  }

  &:last-of-type {
    margin-right: 0 !important;
  }
`;

const SearchTimesButton = styled(Button)`
  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    margin-top: 0.5rem;
  }
`;

const LoadMoreButton = styled(ListGroupItem)`
  margin-top: 0;
  justify-content: center;
  background-color: ${props =>
    props.secondary
      ? props.theme.buttons.secondary.backgroundColor
      : props.theme.buttons.primary.backgroundColor};
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fontFamily};
  font-weight: ${props => props.theme.fontBold};
  text-align: center;
`;

const SEARCH_USERS_QUERY = gql`
  query usersSearchByAdmin($input: GetUsersSearch!) {
    usersSearchByAdmin(input: $input) {
      users {
        patronAccountNumber
        firstName
        lastName
      }
    }
  }
`;

const propTypes = {
  onUserSelect: PropTypes.func.isRequired,
  onSecondUserSelect: PropTypes.func.isRequired
};

function UserAccountSearch({ onUserSelect, onSecondUserSelect }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dataOffset, setDataOffset] = useState(0);
  const [submittedData, setSubmittedData] = useState(null);

  return (
    <Query
      query={SEARCH_USERS_QUERY}
      skip={!submittedData}
      variables={{ input: { ...submittedData, lockedOut: false } }}
    >
      {({ loading, error, data }) => {
        const showClose =
          submittedData &&
          !loading &&
          submittedData.firstName === firstName &&
          submittedData.lastName === lastName;

        return (
          <>
            <Form
              onSubmit={e => {
                e.preventDefault();

                // If submit was clicked with same first/last name as previously,
                // we will clear the results and close the listing.
                if (showClose) {
                  setSubmittedData(null);
                } else {
                  setSubmittedData({ firstName, lastName });
                }
              }}
            >
              <Row className="align-items-end">
                <LargestCol xs="12" lg={'5'}>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    autoComplete="off"
                  />
                </LargestCol>
                <LargestCol xs="12" lg={'5'}>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    autoComplete="off"
                  />
                </LargestCol>
                <LargestCol xs="12" lg="2">
                  <SearchTimesButton
                    block
                    type="submit"
                    disabled={
                      (firstName.trim().length < 2 &&
                        lastName.trim().length < 2) ||
                      loading
                    }
                  >
                    <FontAwesomeIcon icon={showClose ? 'times' : 'search'} />
                    <span className="d-md-none">
                      {showClose ? ' Close Search Results' : ' Search'}
                    </span>
                  </SearchTimesButton>
                </LargestCol>
              </Row>
            </Form>
            {!loading && data && (
              <div>
                {get(data, 'usersSearchByAdmin.users', []).length > 0 ? (
                  <ListGroup>
                    {data.usersSearchByAdmin.users
                      .slice(0, MAX_RESULTS + dataOffset)
                      .map(user => (
                        <ListGroupItem key={user.patronAccountNumber}>
                          <div className="d-flex justify-content-between align-items-center flex-grow-1">
                            <span>{`${user.firstName} ${user.lastName} `}</span>
                            <AccountNumber pill>
                              {user.patronAccountNumber}
                            </AccountNumber>
                          </div>
                          <div className="flex-shrink-0">
                            <SelectUserButton
                              onClick={e => {
                                e.preventDefault();
                                onUserSelect(user);
                                setSubmittedData(null);
                              }}
                            >
                              Use 1st
                            </SelectUserButton>
                            {/* TODO: Uncomment this once second account number field is added back */}
                            {/*<SelectUserButton*/}
                            {/*  onClick={e => {*/}
                            {/*    e.preventDefault();*/}
                            {/*    onSecondUserSelect(user);*/}
                            {/*    setSubmittedData(null);*/}
                            {/*  }}*/}
                            {/*>*/}
                            {/*  Use 2nd*/}
                            {/*</SelectUserButton>*/}
                            <StyledAccountNumber pill>
                              {user.patronAccountNumber}
                            </StyledAccountNumber>
                          </div>
                        </ListGroupItem>
                      ))}
                    <LoadMoreButton
                      tag="button"
                      onClick={() => setDataOffset(dataOffset + MAX_RESULTS)}
                      action
                      disabled={
                        dataOffset >= data.usersSearchByAdmin.users.length
                      }
                    >
                      Load More
                    </LoadMoreButton>
                  </ListGroup>
                ) : (
                  <div>
                    <p>
                      {error
                        ? 'Error loading users'
                        : 'No rewards accounts found.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        );
      }}
    </Query>
  );
}

UserAccountSearch.propTypes = propTypes;

export default UserAccountSearch;
